import { useEffect, useContext, useState } from 'react';
import Dashboards from '@highcharts/dashboards';
import DashboardHeader from './DashboardHeader';
import { DashboardContext } from '../DashboardContext';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { Typography, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  isTreatmentAppointment,
  isSimAppointment,
  isSimAppointmentByString,
  isTreatmentAppointmentByString,
  filterAppointmentDataSources,
} from '../Utils/utils';

const Dashboard = (props: {
  title: string;
  metric: string;
  metricFunction: (data: any[], tenantConfig?: any) => number;
  config: any;
  metricSuffix?: string;
  metricRound?: number;
  dataType?: string;
  subChartDataType?: string;
  appointmentFilterFunction?: (appointmentActivities: string[], appointment: any) => boolean;
  dataSource?: string;
}): JSX.Element => {
  const {
    filteredData,
    previousPeriodData,
    subChartFilteredData,
    dateFilterOverride,
    selectedPointIndex,
    dateRange,
    endDate,
    tenantConfig,
    handleDateClick,
    onChartClick,
  } = useContext(DashboardContext);
  const {
    title,
    config,
    metric,
    metricSuffix = '',
    metricRound = 0,
    dataType = 'CarepathCareplanType',
    subChartDataType = 'CarepathCareplanType',
    appointmentFilterFunction,
    dataSource = 'Lumonus AI',
    metricFunction,
  } = props;

  const [previousPeriodMetric, setPreviousPeriodMetric] = useState<number>(0);
  const [metricChangeIndicator, setMetricChangeIndicator] = useState<string>('');
  const [seeMoreBottomLeft, setSeeMoreBottomLeft] = useState<boolean>(false);
  const [seeMoreBottomRight, setSeeMoreBottomRight] = useState<boolean>(false);
  const [seeMoreUpdated, setSeeMoreUpdated] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);

  const theme = useTheme();

  useEffect(() => {
    let data;
    let subChartData;
    let previousPeriodDataFiltered;

    if (dataType === 'AppointmentWithPractitionersType' && appointmentFilterFunction) {
      // Use backwards compatible filtering
      const useAdvancedFiltering = tenantConfig?.insightsAdvancedAppointmentFiltering;

      if (appointmentFilterFunction === isSimAppointment && !useAdvancedFiltering) {
        // For clinics without advanced filtering, fall back to old string-based filtering for simulations
        const filtered = filterAppointmentDataSources(
          filteredData,
          subChartFilteredData,
          previousPeriodData,
          dataType,
          subChartDataType,
          (appt: any) => isSimAppointmentByString(appt, tenantConfig),
        );
        data = filtered.data;
        subChartData = filtered.subChartData;
        previousPeriodDataFiltered = filtered.previousPeriodDataFiltered;
      } else if (appointmentFilterFunction === isTreatmentAppointment && !useAdvancedFiltering) {
        // For clinics without advanced filtering, fall back to old string-based filtering for treatments
        const filtered = filterAppointmentDataSources(
          filteredData,
          subChartFilteredData,
          previousPeriodData,
          dataType,
          subChartDataType,
          (appt: any) => isTreatmentAppointmentByString(appt, tenantConfig),
        );
        data = filtered.data;
        subChartData = filtered.subChartData;
        previousPeriodDataFiltered = filtered.previousPeriodDataFiltered;
      } else {
        // For clinics with advanced filtering enabled, use new activity-based method
        const getAppointmentActivities = () => {
          if (appointmentFilterFunction === isTreatmentAppointment) {
            return tenantConfig?.insightsTreatmentAppointmentActivities;
          } else if (appointmentFilterFunction === isSimAppointment) {
            return tenantConfig?.insightsSimAppointmentActivities;
          }
          return [];
        };

        const activities = getAppointmentActivities();

        data = filteredData.filter(
          (d: any) => d.__typename === dataType && appointmentFilterFunction(activities, d.appointment),
        );

        subChartData = subChartFilteredData.filter(
          (d: any) => d.__typename === subChartDataType && appointmentFilterFunction(activities, d.appointment),
        );

        previousPeriodDataFiltered = previousPeriodData.filter(
          (d: any) => d.__typename === dataType && appointmentFilterFunction(activities, d.appointment),
        );
      }
    } else {
      // No appointment filtering needed - just filter by dataType
      data = filteredData.filter((d: any) => d.__typename === dataType);
      subChartData = subChartFilteredData.filter((d: any) => d.__typename === subChartDataType);
      previousPeriodDataFiltered = previousPeriodData.filter((d: any) => d.__typename === dataType);
    }

    setData(data);

    Dashboards.board(
      'dashboard-container',
      config({
        theme: theme, // passing in theme because the hook can't be called inside the config function
        data: data,
        subChartData: subChartData,
        filterOverride: dateFilterOverride,
        selectedIndex: selectedPointIndex,
        seeMoreUpdated: seeMoreUpdated,
        seeMoreBottomLeft: seeMoreBottomLeft,
        seeMoreBottomRight: seeMoreBottomRight,
        dateRange: dateRange,
        endDate: endDate,
        tenantConfig: tenantConfig,
        toggleSeeMore: toggleSeeMore,
        onPointClick: handleDateClick,
      }),
    );

    const current = metricFunction.length === 1 ? metricFunction(data) : metricFunction(data, tenantConfig);
    const previous =
      metricFunction.length === 1
        ? metricFunction(previousPeriodDataFiltered)
        : metricFunction(previousPeriodDataFiltered, tenantConfig);

    const percentChange = (Math.abs(current - previous) / previous) * 100;
    setPreviousPeriodMetric(percentChange);
    setMetricChangeIndicator(current > previous ? 'more' : 'less');

    setSeeMoreUpdated(false);
  }, [subChartFilteredData, seeMoreBottomLeft, seeMoreBottomRight]);

  const toggleSeeMore = (chart: string) => {
    setSeeMoreUpdated(true);

    if (chart === 'sub-chart-left') {
      setSeeMoreBottomLeft(!seeMoreBottomLeft);
    } else {
      setSeeMoreBottomRight(!seeMoreBottomRight);
    }

    onChartClick();
  };

  return (
    <Stack height="100%">
      <DashboardHeader title={title} dataSource={dataSource} />
      <Stack
        justifyContent="center"
        alignItems="center"
        fontSize="24px"
        borderRadius="8px"
        marginTop="20px"
        p="20px 0"
        sx={{ backgroundColor: 'background.secondary' }}>
        <Typography variant="h5">
          {metricFunction.length === 1
            ? metricFunction(data).toFixed(metricRound)
            : metricFunction(data, tenantConfig).toFixed(metricRound)}
          {metricSuffix} {metric}
        </Typography>
        <Typography color={theme.palette.text.secondary} height={20}>
          {dateRange !== 'All time' && !isNaN(previousPeriodMetric) && isFinite(previousPeriodMetric) && (
            <Stack alignItems="center" direction="row">
              <ArrowForward
                style={{
                  zIndex: 1,
                  marginRight: '5px',
                  transform: `rotate(${metricChangeIndicator === 'more' ? '-' : ''}90deg)`,
                  width: '16px',
                }}
              />
              {previousPeriodMetric.toFixed(metricRound)}% {metricChangeIndicator} than previous{' '}
              {dateRange.split('Past ')[1]}
            </Stack>
          )}
        </Typography>
      </Stack>
      <Stack id="dashboard-container" flex={1} className="highcharts-light" />
    </Stack>
  );
};

export default Dashboard;
